/**
 * Footer legal pages.
 *
 * Six documents, each rendered by the same component from a content map:
 *   - pricing / fiction / terms / refund / contact — standalone hash routes (kept)
 *   - privacy — firstbid-shaped privacy policy PLUS the other topics as sections
 *     so the compact footer only needs a Privacy Policy link
 *
 * No fake metrics, no charity framing. The product is a ranked listing.
 */

export const LEGAL_PAGES = [
  { slug: "pricing", title: "Pricing" },
  { slug: "fiction", title: "Fiction Disclaimer" },
  { slug: "terms", title: "Terms of Service" },
  { slug: "privacy", title: "Privacy Policy" },
  { slug: "refund", title: "Refund Policy" },
  { slug: "contact", title: "Contact" },
];

export function readLegalSlug(hash = "") {
  const m = String(hash || "").match(/^#\/legal\/([a-z]+)$/i);
  if (!m) return null;
  const slug = m[1].toLowerCase();
  return LEGAL_PAGES.some((p) => p.slug === slug) ? slug : null;
}

const PRICING = [
  "GetMeHome.lol is a pay-to-rank leaderboard. Your payment buys a ranked listing for the URL you submit. It is not a donation, a contribution to a cause, a charity gift, or an investment.",
  "Minimum bid is $1 (Pay What You Want). You can outbid any existing listing by paying one dollar more than its current total. Paying the same URL again stacks onto that listing instead of creating a duplicate.",
  "Payments are processed by Dodo. The amount you choose is the amount added to your listing's rank. We do not add a GetMeHome surcharge, subscription, or hidden tier.",
  "Rankings update only after a verified payment webhook from the checkout provider. Reaching the top of the board does not entitle you to anything beyond the listing placement itself.",
];

const FICTION = [
  "GetMeHome.lol is a work of fiction and an art project. The story of 'Sam' and the stranded ship is a skin wrapped around a pay-to-rank leaderboard. It is not a real rescue, a fundraiser for a person, or a charity.",
  "Payments buy a ranked listing on this site. They are not a donation, charity, investment, or rescue. No real person is being rescued.",
  "No part of this site implies that funds reach an individual in need. The narrative is decorative. The purchasable object is the listing placement.",
];

const TERMS = [
  "By submitting a URL and payment you agree that you are purchasing a ranked listing for that URL. You represent that you have the right to promote that URL and that its content does not infringe third-party rights or laws.",
  "Listings may be removed at our discretion if they link to illegal, abusive, or infringing content. Removed listings are not automatically refunded; see the Refund Policy.",
  "Rankings reflect cumulative verified payments and can be overtaken by other participants at any time. We make no guarantee of position, duration, or visibility.",
  "This service is provided as-is, without warranty. The fiction is intentional; the leaderboard is real.",
];

const REFUND = [
  "Payments are final once the listing is promoted by a verified webhook, because rank placement is consumed immediately. If a payment is taken but the webhook fails to promote your listing, contact us and we will either promote it or refund.",
  "Listings removed for policy violations are reviewed case by case and may be refunded at our discretion.",
  "To request a refund, email getmehome.support@gmail.com with your listing URL and the email used at checkout.",
];

const CONTACT = [
  "Email: getmehome.support@gmail.com. X: @singlesouup (https://x.com/singlesouup).",
  "For a listing removal, refund question, or data deletion request, include the exact URL you submitted and the email used at checkout when relevant.",
  "This is a small art project run by one person; replies may take a few days.",
];

const PRIVACY_CORE = [
  "Your privacy matters. This policy explains how GetMeHome.lol collects, uses, and safeguards information when you use the site.",
  "1. Overview & information we collect",
  "GetMeHome is built for a public pay-to-rank leaderboard. We collect only what we need to operate the board and process payments.",
  "Listing information: when you pay for a spot we store the submitted website URL, a normalized domain, and public metadata (title, description, image) auto-pulled from the page so your listing can be shown on the board.",
  "Payment information: payments are processed by Dodo. We do not store credit card numbers, bank credentials, or full billing details on our servers. We keep payment status, amount, and provider references needed to verify the purchase and rank the listing.",
  "Usage: we may use essential hosting and request logs from our infrastructure providers (Cloudflare for the frontend, Render for the API, Supabase for the database). We do not load advertising trackers or sell visitor data. This site does not currently load a third-party analytics pixel.",
  "Click metrics: we count clicks on listed URLs (via /go/:id) so the board can show anonymized click totals.",
  "Visit counts: we keep a first-party running total of unique visits (at most once per browser per day) so we can show how many people have opened the site. This starts at zero, is never seeded, and is not a live viewer count. We do not use it for ads and we do not sell it.",
  "2. How we use your information",
  "To publish, rank, and display paid listings on GetMeHome.",
  "To verify payments and promote a listing only after a confirmed webhook from the checkout provider.",
  "To calculate board totals, ranks, and click counts.",
  "To protect the platform against spam, abuse, fraud, or policy violations.",
  "3. Information sharing & third parties",
  "We never sell, rent, or trade personal information for marketing.",
  "Payment processor: we share transaction data with Dodo only to create checkouts and verify payments.",
  "Infrastructure: Cloudflare (static hosting), Render (API), and Supabase (Postgres) process data needed to run the service.",
  "Legal compliance: we may disclose information if required by law or valid legal process.",
  "4. Cookies and local storage",
  "We do not use invasive advertising cookies or cross-site tracking pixels.",
  "We may use local or session storage for preferences (for example music on/off), a one-shot UI flag after a successful checkout (for example a short celebration animation), and a same-day flag so we count your visit at most once per day. These are not used for advertising.",
  "5. Data retention & your rights",
  "Paid listings and payment records are kept as part of the public leaderboard history until removed.",
  "To remove or update a listing, or to exercise privacy rights, email getmehome.support@gmail.com. We will delete stored URL metadata for your listing on request where feasible; payment records needed for accounting or fraud prevention may be retained as required.",
  "6. Changes to this policy",
  "We may update this Privacy Policy from time to time. Changes will be posted on this page.",
  "Questions or concerns? Email getmehome.support@gmail.com.",
];

/** Privacy page also carries the old footer topics so the compact footer only links here. */
const PRIVACY_WITH_FOOTER_TOPICS = [
  ...PRIVACY_CORE,
  "Pricing",
  ...PRICING,
  "Fiction Disclaimer",
  ...FICTION,
  "Terms of Service",
  ...TERMS,
  "Refund Policy",
  ...REFUND,
  "Contact",
  ...CONTACT,
];

export const COPY = {
  pricing: PRICING,
  fiction: FICTION,
  terms: TERMS,
  privacy: PRIVACY_WITH_FOOTER_TOPICS,
  refund: REFUND,
  contact: CONTACT,
};

export function LegalPage({ page }) {
  const meta = LEGAL_PAGES.find((p) => p.slug === page);
  const title = meta ? meta.title : "Legal";
  const paragraphs = COPY[page] || ["This page is not available."];

  return (
    <section
      data-testid={`legal-page-${page}`}
      className="mx-auto max-w-2xl px-4 py-12"
    >
      <a href="#top" data-testid="legal-back" className="mono text-xs text-gmh-cyan hover:underline">
        &larr; Back to board
      </a>
      <h1
        data-testid={`legal-heading-${page}`}
        className="mt-6 text-2xl font-semibold tracking-tight text-gmh-orange"
      >
        {title}
      </h1>
      <div
        data-testid={`legal-body-${page}`}
        className="mt-6 space-y-4 text-sm leading-relaxed text-slate-300"
      >
        {paragraphs.map((p, i) => {
          const isSection =
            /^(Pricing|Fiction Disclaimer|Terms of Service|Refund Policy|Contact|\d+\. )/.test(p) ||
            p === "1. Overview & information we collect" ||
            p.startsWith("2. How we use") ||
            p.startsWith("3. Information sharing") ||
            p.startsWith("4. Cookies") ||
            p.startsWith("5. Data retention") ||
            p.startsWith("6. Changes");
          return isSection ? (
            <h2 key={i} className="pt-2 text-base font-semibold text-slate-100">
              {p}
            </h2>
          ) : (
            <p key={i}>{p}</p>
          );
        })}
      </div>
    </section>
  );
}

export default LegalPage;
